import {
	ActionReducerMapBuilder,
	createSlice,
	isAnyOf,
	PayloadAction,
} from "@reduxjs/toolkit";

import { isStringEmpty } from "drh-js-library/dist/drh-string-utils/is-string-empty";
import { searchApi } from "../../../system/redux/api/search-api";
import { searchQmiApi } from "../../../system/redux/api/search-qmi-api";
import { SearchApiResults } from "../../../system/redux/api/interfaces/search-api-results";
import { FacetAppState } from "../../models/state/facet-app-state";
import allFacets, {
	drhFacetAmenity,
	drhFacetBathrooms,
	drhFacetBedrooms,
	drhFacetGarages,
	drhFacetHomeSeries,
	drhFacetPriceRange,
	drhFacetPropertyType,
	drhFacetSize,
	drhFacetStories,
} from "../../constants/all-facets";
import { FacetStateValues } from "../../models/state/facet-state-values";
import { getNewSelectedFacetValues } from "../../functions/get-new-selected-facet-values";
import { RangeValues } from "../../models/types/type-range-values";
import { FacetBase } from "../../models/facet-base/facet-base";
import { FacetTypes } from "../../constants/facet-types-enum";
import { getFacetFromFieldName } from "../../functions/get-facet-from-field-name";
import { urlHasFacetInfo } from "../../functions/url/url-has-facet-info";
import { parseFacetUrlParams } from "../../functions/url/parse-facet-url-params";
import { UnderlineIcon } from "@radix-ui/react-icons";
import { getFacetFromUrlParam } from "../../functions/url/get-facet-from-url-param";

export const facetSlice = createSlice({
	name: "facets",
	initialState: {
		modalOpen: false,
		initialFacets: {},
		selectedFacets: {},
	} as FacetAppState,

	reducers: {
		//Set the initial state from the URL
		setInitialFacetStateFromUrl: (state, action: PayloadAction<string>) => {
			//Facets
			if (urlHasFacetInfo(action.payload)) {
				let facetValuesFromUrl = parseFacetUrlParams(action.payload);

				if (facetValuesFromUrl.length > 0) {
					facetValuesFromUrl.forEach((row) => {
						if (!isStringEmpty(row)) {
							let facetName = row[0].toLowerCase();
							let facetValue = row[1];

							let facet = getFacetFromUrlParam(facetName, allFacets);

							if (facet !== null && facet !== undefined) {
								if (facet.type !== null) {
									let processedValue;

									switch (facet.type) {
										case FacetTypes.Number:
											processedValue = parseInt(facetValue) as number;
											break;

										case FacetTypes.Range:
											let rangeParts = facetValue.split(",");
											if (rangeParts.length > 0) {
												let lowNumber = parseInt(rangeParts[0]);
												let highNumber = parseInt(rangeParts[1]);

												processedValue = [lowNumber, highNumber] as RangeValues;
											}
											break;

										case FacetTypes.Text:
											let textParts = facetValue.split(",");
											if (textParts.length > 0) {
												processedValue = textParts as string[];
											}

											break;
									}

									if (processedValue !== undefined) {
										let newlySelectedFacets = getNewSelectedFacetValues(
											state.selectedFacets,
											state.initialFacets,
											facet,
											processedValue,
										);

										state.selectedFacets = newlySelectedFacets;
									}
								}
							}
						}
					});
				}
			}
		},

		//Facets
		setFacetValue: (
			state,
			action: PayloadAction<{
				facet: FacetBase;
				facetValues: string[] | number | RangeValues;
			}>,
		) => {
			state.selectedFacets = getNewSelectedFacetValues(
				state.selectedFacets,
				state.initialFacets,
				action.payload.facet,
				action.payload.facetValues,
			);
		},
		clearFacets: (state, action: PayloadAction<null>) => {
			state.selectedFacets = {};
		},
		setFacetModalOpen: (state, action: PayloadAction<boolean>) => {
			state.modalOpen = action.payload;
		},
	},
	extraReducers: (builder: ActionReducerMapBuilder<FacetAppState>) => {
		builder.addMatcher(
			//This adds a listener to the getApiResults call, when that is fulfilled this code will be called
			//so we can process the results.
			isAnyOf(
				searchApi.endpoints.getApiResults.matchFulfilled,
				searchQmiApi.endpoints.getQmiApiResults.matchFulfilled
			),
			(state, action) => {

				let searchApiResults = action.payload as SearchApiResults;

				try {
					//Facets
					let visibleFacets = {} as FacetStateValues;

					//Number Facets
					visibleFacets[drhFacetBathrooms.fieldName] =
						searchApiResults.FacetData.BathsFacet.max;

					visibleFacets[drhFacetBedrooms.fieldName] =
						searchApiResults.FacetData.BedsFacet.max;

					visibleFacets[drhFacetGarages.fieldName] =
						searchApiResults.FacetData.CarsFacet.max;

					visibleFacets[drhFacetStories.fieldName] =
						searchApiResults.FacetData.StoriesFacet.max;

					//Range Facets
					var roundedMinPrice = Math.floor(searchApiResults.FacetData.PriceFacet.min / 50) * 50;
					var roundedMaxPrice = Math.ceil(searchApiResults.FacetData.PriceFacet.max / 50) * 50;
					visibleFacets[drhFacetPriceRange.fieldName] = [
						roundedMinPrice,
						roundedMaxPrice,
					];

					var roundedMinSqft = Math.floor(searchApiResults.FacetData.SqFtFacet.min / 50) * 50;
					var roundedMaxSqft = Math.ceil(searchApiResults.FacetData.SqFtFacet.max / 50) * 50;
					visibleFacets[drhFacetSize.fieldName] = [
						roundedMinSqft,
						roundedMaxSqft,
					];

					//Text Facets
					visibleFacets[drhFacetAmenity.fieldName] =
						searchApiResults.FacetData.AmenityFacet.Amenities;

					visibleFacets[drhFacetPropertyType.fieldName] =
						searchApiResults.FacetData.PropertyFacet.PropertyTypes;

					visibleFacets[drhFacetHomeSeries.fieldName] =
						searchApiResults.FacetData.SeriesFacet.SeriesTypes;

					state.initialFacets = visibleFacets;
				} catch (exc) {
					console.log("searchApi.endpoints.getResults.matchFulfilled ERROR");
					console.log(exc);
				}
			},
		);
	},
});

export const {
	//Initialize App
	setInitialFacetStateFromUrl,

	//Faceting
	setFacetValue,
	clearFacets,

	//Modal
	setFacetModalOpen,
} = facetSlice.actions;

export default facetSlice.reducer;
